import React, { useContext, useState } from "react";
import {
  Box,
  Stack,
  Text,
  Button,
  Anchor,
  Group,
  ActionIcon,
  useMantineTheme,
} from "@mantine/core";
import {
  IconArrowRight,
  IconShieldCheck,
  IconBuilding,
  IconX,
} from "@tabler/icons-react";
import VXLogo from "../assets/vx-logo.svg";
import useLocale from "../hooks/useLocale";
import { DataContext } from "../context/DataContext";
import { resolveOnAccentColor } from "../utils/onAccentColor";

const readPrimaryColorFromUrl = () => {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("primaryColor");
  if (!raw) return null;
  return raw.startsWith("#") ? raw : `#${raw}`;
};

// Centered-hero header used by the "centered" variant. Three
// circles — customer / shield-with-halo / Veratad — with hairline
// connector segments. The middle shield gets a primary-color
// disc + a soft pulsing halo ring so the eye lands on it. Below
// the circles: big centered headline + a two-line partnership
// statement with the customer name colored in their brand-adjacent
// hue and "Veratad" in the primary color.
const CenteredHero = ({
  customerName,
  customerLogoUrl,
  accentColor,
  onAccentColor,
  borderColor,
  cardBg,
  mutedText,
  watermarkColor,
  titleColor,
  locale,
}) => {
  const title = locale?.header?.centeredTitle || "Verification required";
  const lead =
    locale?.header?.centeredPartnershipLead ||
    "{{customerName}} has partnered with {{veratad}}";
  const detail =
    locale?.header?.centeredPartnershipDetail ||
    "to verify your age and/or identity.";

  const renderLead = () =>
    lead
      .split(/(\{\{customerName\}\}|\{\{veratad\}\})/)
      .map((token, i) => {
        if (token === "{{customerName}}") {
          // Render the customer name bold but in the same color
          // as the surrounding subtitle — no per-publisher brand
          // tint so the paragraph reads as one uniform line of
          // text instead of a colored highlight inside it.
          return (
            <Text span fw={700} key={`lead-${i}`}>
              {customerName}
            </Text>
          );
        }
        if (token === "{{veratad}}") {
          // Same treatment as customerName — bold for emphasis,
          // no color override so it sits with the surrounding
          // text instead of pulling focus.
          return (
            <Text span fw={700} key={`lead-${i}`}>
              Veratad
            </Text>
          );
        }
        return token;
      });

  return (
    <Box style={{ position: "relative", width: "100%" }}>
      {/* Hero row */}
      <Group spacing={0} mb={28} noWrap align="center" position="center">
        <CircleLogo
          src={customerLogoUrl}
          alt={customerName}
          fallbackIcon={IconBuilding}
          borderColor={borderColor}
          cardBg={cardBg}
          mutedText={mutedText}
        />
        <ConnectorLine borderColor={borderColor} width={32} />
        <ShieldWithHalo accentColor={accentColor} onAccentColor={onAccentColor} />
        <ConnectorLine borderColor={borderColor} width={32} />
        <CircleLogo
          src={VXLogo}
          alt="Veratad"
          borderColor={borderColor}
          cardBg={cardBg}
          mutedText={mutedText}
        />
      </Group>

      {/* Big centered headline */}
      <Text
        fw={800}
        fz={26}
        lh={1.2}
        ta="center"
        c={titleColor}
        mb={8}
        style={{
          letterSpacing: "-0.02em",
          whiteSpace: "pre-line",
        }}
      >
        {title}
      </Text>

      {/* Smaller, lighter subtitle — the partnership statement
          becomes supporting context for the title above. Single
          balanced paragraph so the lead and detail flow without
          orphan words. Customer name keeps its brand color inline. */}
      <Text
        fz={17}
        fw={400}
        lh={1.5}
        ta="center"
        c={mutedText}
        style={{
          textWrap: "balance",
          maxWidth: 320,
          marginInline: "auto",
        }}
      >
        {renderLead()} {detail}
      </Text>
    </Box>
  );
};

// Background pattern layer for the "centered" variant — a tiled
// dot grid that sits absolutely behind all of the consent body
// (hero, subtitle, consent text, button) so the whole screen
// reads as gently textured. `pointer-events: none` keeps content
// above it interactive.
const CenteredBackground = ({ color }) => (
  <Box
    aria-hidden="true"
    style={{
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      overflow: "hidden",
      zIndex: 0,
      backgroundImage: `radial-gradient(${color} 1.4px, transparent 1.8px)`,
      backgroundSize: "16px 16px",
    }}
  />
);

const CircleLogo = ({ src, alt, fallbackIcon, borderColor, cardBg, mutedText }) => {
  const FallbackIcon = fallbackIcon || IconBuilding;
  return (
    <Box
      style={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        backgroundColor: cardBg,
        border: `1px solid ${borderColor}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        flexShrink: 0,
        position: "relative",
        zIndex: 1,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt || ""}
          style={{ maxWidth: 44, maxHeight: 44, objectFit: "contain" }}
        />
      ) : (
        <FallbackIcon size={28} stroke={1.6} color={mutedText} />
      )}
    </Box>
  );
};

const ConnectorLine = ({ borderColor, width }) => (
  <Box
    style={{
      width,
      height: 2,
      backgroundColor: borderColor,
      flexShrink: 0,
      margin: "0 -6px",
    }}
  />
);

const ShieldWithHalo = ({ accentColor, onAccentColor }) => (
  <Box
    style={{
      position: "relative",
      width: 80,
      height: 80,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {/* Halo rings — two staggered pulsing rings give the shield
        a "verified / trustworthy" energy without being noisy. */}
    {[0, 1].map((i) => (
      <Box
        key={`halo-${i}`}
        sx={{
          position: "absolute",
          width: 56,
          height: 56,
          borderRadius: "50%",
          border: `1.5px solid ${accentColor}`,
          opacity: 0,
          animation: `consentShieldHalo 2.4s ease-out ${i * 1.2}s infinite both`,
          "@keyframes consentShieldHalo": {
            "0%": { transform: "scale(0.95)", opacity: 0.55 },
            "100%": { transform: "scale(1.6)", opacity: 0 },
          },
        }}
      />
    ))}
    {/* Shield disc */}
    <Box
      style={{
        position: "relative",
        zIndex: 1,
        width: 56,
        height: 56,
        borderRadius: "50%",
        backgroundColor: accentColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `0 6px 20px ${accentColor}55`,
      }}
    >
      <IconShieldCheck size={28} stroke={2} color={onAccentColor} />
    </Box>
  </Box>
);

/**
 * Single-screen notice + consent flow. All copy is pulled verbatim from
 * the existing `biometric-consent` locale.
 *
 * Main screen: partnership hero, page title, the intro paragraph, a
 * "Read more" link, and the Consent button.
 *
 * Modal (opened by the Read more link): the remaining four legal
 * paragraphs (collect, analysis+provider, retention+biometric+privacy
 * with the publisher and Veratad privacy-policy links) plus the
 * binding acknowledgment in a primary-color-tinted callout.
 *
 * `modalOpen` is controllable from the outside for storybook/testing;
 * if not provided the component manages it internally.
 */
const NoticeConsentPrompt = ({
  modalOpen: modalOpenProp,
  defaultModalOpen = false,
  primaryColor,
  // Publisher's filled-button label color; falls back to DataContext.
  buttonTextColor,
  customerName = "{Company Name}",
  customerLogoUrl,
  customerPrivacyPolicyUrl,
  veratadPrivacyNoticeUrl,
  requireConsent = false,
  // "centered" (default) — premium centered hero: three logos
  // with a halo ring around the middle shield, big headline,
  // smaller balanced subtitle (customer name and "Veratad"
  // bold in the same color as the surrounding text), faint
  // dot-grid background. The app's chrome renders the footer.
  // "partnership" — legacy left-aligned hero + long partnership
  // headline + tight body.
  // "minimal" — Persona-style: small customer wordmark, short
  // title, generous empty space, body pushed to bottom, pill
  // button, and a small "Secured by Veratad" footer badge.
  // Same modal + same summary body text — only the chrome
  // around it changes.
  variant = "centered",
  onContinue,
  onModalOpenChange,
}) => {
  const isMinimal = variant === "minimal";
  const isCentered = variant === "centered";
  const theme = useMantineTheme();
  const locale = useLocale("biometric-consent");
  const [internalModalOpen, setInternalModalOpen] = useState(defaultModalOpen);
  const modalOpen = modalOpenProp ?? internalModalOpen;

  const cardBg =
    theme.colorScheme === "dark" ? theme.colors.dark[6] : "#fff";
  const accentColor =
    primaryColor ||
    readPrimaryColorFromUrl() ||
    theme.colors[theme.primaryColor]?.[6] ||
    theme.colors.blue[6];
  // Shields drawn on the accent disc occupy the same visual role as a
  // filled Button's label, so they follow the publisher's button text
  // color (`styles.components.button.color`). With no override we pick
  // the readable foreground for the accent rather than always white,
  // which vanishes on a light primary. useContext returns undefined
  // safely outside a DataContext provider (Storybook stories).
  const dataContext = useContext(DataContext);
  const onAccentColor = resolveOnAccentColor({
    buttonTextColor:
      buttonTextColor ||
      dataContext?.data?.styles?.components?.button?.color,
    accent: accentColor,
  });
  const borderColor =
    theme.colorScheme === "dark"
      ? theme.colors.dark[4]
      : theme.colors.gray[3];
  const mutedText =
    theme.colorScheme === "dark"
      ? theme.colors.dark[2]
      : theme.colors.gray[7];
  const watermarkColor =
    theme.colorScheme === "dark"
      ? "rgba(255,255,255,0.04)"
      : "rgba(15, 23, 42, 0.05)";
  // Inline links (Privacy Policy, Read more) render as black-with-
  // underline rather than primary-color so they don't compete with
  // the publisher's brand color on the customer name or the CTA.
  // Theme-aware so dark mode flips to white.
  const linkColor =
    theme.colorScheme === "dark" ? theme.white : theme.black;
  const linkStyle = { textDecoration: "underline" };

  const setModalOpen = (next) => {
    if (modalOpenProp === undefined) setInternalModalOpen(next);
    if (onModalOpenChange) onModalOpenChange(next);
  };

  const handleContinue = () => {
    if (onContinue) {
      onContinue({
        continued: true,
        timestamp: new Date().toISOString(),
        requireConsentChecked: requireConsent,
      });
    }
  };

  // Renders the {{customerName}} / {{veratad}} / {{privacyPolicy}} /
  // {{readMore}} placeholders inline. customerName / Veratad are bold
  // spans; privacyPolicy is a link to the customer's privacy URL;
  // readMore is a button-style link that opens the deep-dive modal.
  const renderTokenized = (template, keyPrefix, plain = false) =>
    (template || "")
      .split(
        // Match the possessive form `{{customerName}}'s` (both
        // straight `'` and curly `’` apostrophes) before the
        // plain `{{customerName}}` so the `'s` gets absorbed into
        // the same bold span. Without this, the apostrophe-s sits
        // in unstyled text after the customer span and reads as
        // an orphan `'s` next to a bold name. The English locale
        // uses curly `’` — translations may use either.
        /(\{\{customerName\}\}[’']s|\{\{customerName\}\}|\{\{veratad\}\}|\{\{privacyPolicy\}\}|\{\{readMore\}\})/,
      )
      .map((token, i) => {
        const isCustomerToken =
          token === "{{customerName}}" ||
          token === "{{customerName}}'s" ||
          token === "{{customerName}}’s";
        if (isCustomerToken) {
          const possessive = token !== "{{customerName}}";
          const display = possessive ? `${customerName}’s` : customerName;
          if (isMinimal || plain) {
            // Minimal variant and plain mode (the legal consent
            // summary) intentionally avoid inline bold to keep the
            // body text feeling calm and uniform.
            return (
              <React.Fragment key={`${keyPrefix}-${i}`}>
                {display}
              </React.Fragment>
            );
          }
          return (
            <Text
              span
              fw={700}
              key={`${keyPrefix}-${i}`}
              c={theme.colorScheme === "dark" ? "white" : "dark"}
            >
              {display}
            </Text>
          );
        }
        if (token === "{{veratad}}") {
          if (isMinimal || plain) {
            return (
              <React.Fragment key={`${keyPrefix}-${i}`}>Veratad</React.Fragment>
            );
          }
          return (
            <Text
              span
              fw={700}
              key={`${keyPrefix}-${i}`}
              c={theme.colorScheme === "dark" ? "white" : "dark"}
            >
              Veratad
            </Text>
          );
        }
        if (token === "{{privacyPolicy}}") {
          const label =
            locale.intro?.privacyPolicyText || "Privacy Policy";
          if (!customerPrivacyPolicyUrl) {
            return (
              <Text
                span
                fw={plain ? 400 : 600}
                key={`${keyPrefix}-${i}`}
                c={plain ? "dimmed" : theme.colorScheme === "dark" ? "white" : "dark"}
              >
                {label}
              </Text>
            );
          }
          return (
            <Anchor
              key={`${keyPrefix}-${i}`}
              href={customerPrivacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              c={linkColor}
              fw={plain ? 400 : 600}
              style={linkStyle}
            >
              {label}
            </Anchor>
          );
        }
        if (token === "{{readMore}}") {
          return (
            <Anchor
              component="button"
              type="button"
              key={`${keyPrefix}-${i}`}
              onClick={() => setModalOpen(true)}
              c={linkColor}
              fw={600}
              style={linkStyle}
            >
              {locale.intro?.readMore}
            </Anchor>
          );
        }
        return token;
      });

  const summaryTemplate = requireConsent
    ? locale.intro?.consentSummaryOutside
    : locale.intro?.consentSummaryInside;

  // Bleed past the parent's padding so the screen fills edge to edge
  // inside the journey iframe — matches PasskeyPrompt's outer frame.
  // `position: relative` anchors the in-component Read-more overlay so
  // it stays within the component's bounds instead of covering the
  // whole viewport like a portal-based Modal would.
  const outerStyle = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "calc(100% + 20px)",
    margin: "-20px -25px 0 -25px",
  };

  return (
    <Box style={outerStyle}>
        {/* Faint shield + dot-grid background — only for the
            centered variant. Sits behind every layout slot in the
            outer container so the entire screen feels textured,
            not just a single decorative band. */}
        {isCentered && <CenteredBackground color={watermarkColor} />}

        <Box
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "24px 20px 12px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: 1,
          }}
        >
          {isCentered ? (
            <>
              <CenteredHero
                customerName={customerName}
                customerLogoUrl={customerLogoUrl}
                accentColor={accentColor}
                onAccentColor={onAccentColor}
                borderColor={borderColor}
                cardBg={cardBg}
                mutedText={mutedText}
                watermarkColor={watermarkColor}
                titleColor={theme.colorScheme === "dark" ? theme.white : theme.black}
                locale={locale}
              />
              {/* Flex spacer — pushes the consent acknowledgment
                  paragraph down to sit just above the button, so
                  the middle of the screen becomes the watermark's
                  breathing room. */}
              <Box style={{ flex: 1, minHeight: 80 }} />
            </>
          ) : isMinimal ? (
            <>
              {/* Minimal wordmark: customer logo if provided, else
                  customer name as a small bold text mark. Sits in
                  the top-left like Persona's "OpenAI" header. */}
              <Box mb={28}>
                {customerLogoUrl ? (
                  <img
                    src={customerLogoUrl}
                    alt={customerName}
                    style={{ maxHeight: 28, maxWidth: 160, objectFit: "contain" }}
                  />
                ) : (
                  <Text
                    fw={700}
                    fz={18}
                    c={theme.colorScheme === "dark" ? "white" : "dark"}
                  >
                    {customerName}
                  </Text>
                )}
              </Box>

              {/* Short, declarative title — period included like
                  Persona's "Additional information required." */}
              <Text fw={700} fz={26} lh={1.2} ta="start" mb={20}>
                {locale.header?.minimalTitle ||
                  "Additional information required."}
              </Text>

              {/* Big breathing room — the whole point of the
                  minimal variant is the empty space between title
                  and body. */}
              <Box style={{ flex: 1, minHeight: 80 }} />
            </>
          ) : (
            <>
              {/* Partnership hero: customer logo ── ✓ ── Veratad logo */}
              <Group spacing={0} mb={16} noWrap align="center" position="left">
                <Box
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {customerLogoUrl ? (
                    <img
                      src={customerLogoUrl}
                      alt=""
                      style={{ maxWidth: 38, maxHeight: 38, objectFit: "contain" }}
                    />
                  ) : (
                    <IconBuilding size={26} stroke={1.6} color={mutedText} />
                  )}
                </Box>

                <Box
                  style={{
                    position: "relative",
                    width: 64,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    style={{
                      height: 2,
                      width: "100%",
                      backgroundColor: borderColor,
                    }}
                  />
                  <Box
                    style={{
                      position: "absolute",
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: accentColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 3px 10px ${accentColor}40`,
                    }}
                  >
                    <IconShieldCheck size={16} stroke={2} color={onAccentColor} />
                  </Box>
                </Box>

                <Box
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={VXLogo}
                    alt="Veratad"
                    style={{ maxWidth: 34, maxHeight: 34, objectFit: "contain" }}
                  />
                </Box>
              </Group>

              {/* Title: partnership statement */}
              <Text fw={700} fz={20} lh={1.25} ta="start" mb={20}>
                {renderTokenized(locale.header?.subtitle, "subtitle")}
              </Text>

              <Box style={{ flex: 1 }} />
            </>
          )}

          {/* Single consolidated consent summary — wraps the
              acknowledgment, the privacy-policy link, and a "Read
              more" trigger into one paragraph. Variant flips on
              `requireConsent` (Inside US vs. Outside US). All of the
              old verbose intro / privacy / acknowledgment text is
              still available, now inside the Read More modal. */}
          <Text fz={13} c="dimmed" lh={1.55}>
            {renderTokenized(summaryTemplate, "summary", true)}
          </Text>
        </Box>

        <Box
          style={{
            padding: "12px 20px 16px",
            flexShrink: 0,
            marginTop: "auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Button
            size="lg"
            fullWidth
            onClick={handleContinue}
            radius={isMinimal ? "xl" : undefined}
            rightIcon={isMinimal ? undefined : <IconArrowRight size={18} className="rtl-mirror" />}
          >
            {requireConsent
              ? locale.footer?.continue
              : locale.footer?.consentAndContinue}
          </Button>

          {/* Minimal footer: "Secured by [Veratad VX logo]". Mirrors
              Persona's "SECURED WITH persona" badge on the bottom-
              right of the card. Only renders in the minimal variant
              since the partnership variant already advertises
              Veratad in its hero. */}
          {isMinimal && (
            <Group
              position="right"
              spacing={6}
              noWrap
              align="center"
              mt={14}
            >
              <Text fz={10} fw={600} c="dimmed" tt="uppercase" lts={0.6}>
                {locale.header?.minimalSecured || "Secured by"}
              </Text>
              <img
                src={VXLogo}
                alt="Veratad"
                style={{ height: 14, objectFit: "contain" }}
              />
              <Text
                fz={11}
                fw={700}
                c={theme.colorScheme === "dark" ? "white" : "dark"}
              >
                Veratad
              </Text>
            </Group>
          )}
        </Box>

        {/* In-component Read more overlay: a small dialog absolutely
            positioned within the outer Box so it stays contained to
            the journey iframe rather than covering the whole viewport
            like a portal Modal would. */}
        {modalOpen && (
          <Box
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              zIndex: 20,
            }}
            onClick={() => setModalOpen(false)}
          >
            <Box
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxHeight: "100%",
                backgroundColor: cardBg,
                borderRadius: 14,
                boxShadow: "0 12px 36px rgba(0, 0, 0, 0.25)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Title removed — just a close X aligned to the top
                  right. The modal content speaks for itself. */}
              <Group
                position="right"
                spacing={0}
                noWrap
                style={{
                  padding: "10px 10px 0 10px",
                  flexShrink: 0,
                }}
              >
                <ActionIcon
                  variant="subtle"
                  onClick={() => setModalOpen(false)}
                  aria-label={locale.footer?.close}
                >
                  <IconX size={18} stroke={1.8} />
                </ActionIcon>
              </Group>

              <Box
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  padding: "0 16px 16px",
                }}
              >
                <Stack spacing="sm">
                  {/* Partnership/intro paragraph — used to live on
                      the main screen; moved here when the body
                      collapsed to a single consent summary. */}
                  <Text fz={12} lh={1.55}>
                    {locale.intro?.requestedBy}{" "}
                    {customerName}{" "}
                    {locale.intro?.inConnection}{" "}
                    {locale.intro?.thisCompany}{" "}
                    {locale.intro?.determines}
                  </Text>

                  <Text fz={12} lh={1.55}>
                    {locale.collect?.paragraph}
                  </Text>

                  <Text fz={12} lh={1.55}>
                    {locale.analysis?.automated}{" "}
                    {locale.provider?.technology}{" "}
                    {locale.provider?.veratadName}
                    {locale.provider?.onBehalf}{" "}
                    {locale.provider?.requestingCompany}.
                  </Text>

                  <Text fz={12} lh={1.55}>
                    {locale.retention?.text}{" "}
                    {locale.biometric?.callout}
                  </Text>

                  {/* Privacy-policy links — also moved here from
                      the main screen so the modal is the complete
                      deep-dive. */}
                  <Text fz={12} lh={1.55}>
                    {locale.privacy?.intro}{" "}
                    {customerPrivacyPolicyUrl && (
                      <>
                        <Anchor
                          href={customerPrivacyPolicyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          c={linkColor}
                          style={linkStyle}
                        >
                          {customerName} {locale.privacy?.customerPolicy}
                        </Anchor>
                        {" "}{locale.privacy?.joiner}{" "}
                      </>
                    )}
                    <Anchor
                      href={veratadPrivacyNoticeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      c={linkColor}
                      style={linkStyle}
                    >
                      {locale.privacy?.veratadNotice}
                    </Anchor>
                    .
                  </Text>
                </Stack>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
  );
};

export default NoticeConsentPrompt;
